/**
 * useAiChat — hook for global AI chat with SSE streaming and tool execution.
 *
 * Manages:
 * - Conversation state (messages, streaming status)
 * - SSE connection to /v1/ai/chat
 * - Persisting conversations + messages to Dexie
 * - Loading conversation history
 */

import { useState, useCallback, useRef, useEffect } from "react"
import { db } from "@/db/schema"
import { getValidToken, API_PREFIX, clearTokenCache, resetRefreshCooldown, syncToRenewToken } from "@/shared/utils/api/apiClient"
import { logAiRequest } from "@/shared/utils/diagnostics/aiDiagnostics"
import { executeToolCall } from "./aiToolExecutor"
import { readAllForPrompt } from "@/services/ai-memory"
import i18next from "i18next"
import { persistCompressionToMemory, type CompressionState } from "./conversationCompression"
import { planCompression, summarizeRequestMessages } from "./compressionPlan"
import { buildChatRequestBody, buildRequestMessages, chatTurnHeaders, type ApiChatMessage } from "./apiMessages"
import { aiRefusalMessage, aiStreamErrorMessage, AiUserMessageError } from "./aiQuotaDisplay"
import { pinMessage, unpinMessage } from "./conversationMemory"
import type { ToolExecResult } from "./aiToolExecutor"
import type {
  DbChatConversation,
  DbChatMessage,
  ChatToolCall,
  ChatToolResult,
} from "@totodo/operations"

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
const LS_DRAFT_KEY = "totodo:ai-chat-draft"
const LS_CACHE_KEY = "totodo:ai-chat-cache"
const DEXIE_CLOUD_URL =
  import.meta.env.VITE_DEXIE_CLOUD_URL || "https://zfbftppyv.dexie.cloud"

/**
 * Direct token refresh via Dexie Cloud /token endpoint.
 * Bypasses db.cloud.sync() which may be blocked by the sync lock.
 * Falls back when the normal sync-based refresh hangs.
 */
async function directTokenRefresh(): Promise<string | null> {
  const refreshToken = (db.cloud.currentUser?.value as { refreshToken?: string })?.refreshToken
  if (!refreshToken) return null
  try {
    const res = await fetch(`${DEXIE_CLOUD_URL}/token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        scopes: ["ACCESS_DB"],
        time_stamp: Date.now(),
      }),
      signal: AbortSignal.timeout(10_000),
    })
    if (!res.ok) return null
    const data = await res.json() as { accessToken?: string; accessTokenExpiration?: number }
    return data.accessToken || null
  } catch {
    return null
  }
}

/** Context passed with each message to help AI understand what the user is looking at */
export interface ChatContext {
  viewId?: string
  containerId?: string | null
  containerTitle?: string
  containerType?: string
  activeTaskId?: string | null
  activeTaskTitle?: string
  activeTaskNote?: string
  activeTaskTags?: string[]
  activeTaskDueAt?: string
  activeTaskParentPath?: string
}

/** Conversation focus — tracks entities discussed across turns */
export interface ConversationFocus {
  /** Most recently created/modified entity */
  lastEntity: { id: string; title: string; type: string } | null
  /** Implied container (project/list being discussed or viewed) */
  impliedContainer: { id: string; title: string } | null
  /** Recently mentioned entities (max 5, LIFO) */
  recentEntities: Array<{ id: string; title: string; type: string }>
}

/** Extract focus updates from tool results */
function extractFocusFromToolResults(toolResults: ChatToolResult[], currentFocus: ConversationFocus): ConversationFocus {
  const focus = { ...currentFocus, recentEntities: [...currentFocus.recentEntities] }

  for (const tr of toolResults) {
    const res = tr.result as { success?: boolean; data?: Record<string, unknown> } | undefined
    if (!res?.success || !res.data) continue

    const data = res.data
    const id = data.id as string | undefined
    const title = data.title as string | undefined

    if (!id) continue

    // Update lastEntity
    const entityType = inferEntityType(tr.name)
    if (title && entityType) {
      focus.lastEntity = { id, title, type: entityType }
      // Push to recent (dedupe)
      focus.recentEntities = [
        { id, title, type: entityType },
        ...focus.recentEntities.filter((e) => e.id !== id),
      ].slice(0, 5)
    }

    // Update impliedContainer based on tool name
    if (tr.name === "createProject" || tr.name === "createList") {
      focus.impliedContainer = { id, title: title || "" }
    } else if (tr.name === "searchContainers" || tr.name === "listTasks" || tr.name === "getContainers") {
      // If the tool searched/listed a container, that's the implied container
      const items = data.items as Array<{ id: string; title: string }> | undefined
      if (items?.length === 1 && items[0]) {
        focus.impliedContainer = { id: items[0].id, title: items[0].title }
      }
    } else if (tr.name === "moveTask" && data.destinationId) {
      // After moving, the destination becomes implied container
      const destId = data.destinationId as string
      if (destId !== "#inbox" && destId !== "#anyday") {
        focus.impliedContainer = { id: destId, title: (data.destinationTitle as string) || destId }
      }
    }
  }

  return focus
}

function inferEntityType(toolName: string): string | null {
  if (toolName.includes("Task") || toolName === "createTask") return "task"
  if (toolName.includes("Project") || toolName === "createProject") return "project"
  if (toolName.includes("List") || toolName === "createList") return "list"
  if (toolName.includes("Container") || toolName === "searchContainers") return "container"
  return "task" // default for most ops
}

/** Strip _renderData from tool results before sending to AI (it's for local UI only) */
function stripRenderData(toolResults: ChatToolResult[]): ChatToolResult[] {
  return toolResults.map((tr) => {
    const res = tr.result as ToolExecResult | undefined
    if (res && typeof res === "object" && "_renderData" in res) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _renderData, ...clean } = res as ToolExecResult & { _renderData: unknown }
      return { ...tr, result: clean }
    }
    return tr
  })
}

/** One chat message as POST /ai/chat takes it. */
function toApiMessage(m: ChatMessage): ApiChatMessage {
  return {
    role: m.role,
    content: m.content,
    attachments: m.attachments,
    toolCalls: m.toolCalls,
    toolResults: m.toolResults ? stripRenderData(m.toolResults) : undefined,
  }
}

// ---------------------------------------------------------------------------
// localStorage draft helpers
// ---------------------------------------------------------------------------

function saveDraftToLocalStorage(messages: ChatMessage[], conversationId: string | null) {
  try {
    localStorage.setItem(LS_DRAFT_KEY, JSON.stringify({ conversationId, messages, savedAt: Date.now() }))
  } catch { /* quota exceeded — ignore */ }
}

function loadDraftFromLocalStorage(): { conversationId: string | null; messages: ChatMessage[] } | null {
  try {
    const raw = localStorage.getItem(LS_DRAFT_KEY)
    if (!raw) return null
    const draft = JSON.parse(raw) as { conversationId: string | null; messages: ChatMessage[]; savedAt: number }
    // Expire drafts older than 24h
    if (Date.now() - draft.savedAt > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(LS_DRAFT_KEY)
      return null
    }
    return draft
  } catch {
    return null
  }
}

function clearDraftFromLocalStorage() {
  localStorage.removeItem(LS_DRAFT_KEY)
}

// ---------------------------------------------------------------------------
// localStorage CACHE — persists last conversation for instant reload
// ---------------------------------------------------------------------------

function saveCacheToLocalStorage(messages: ChatMessage[], conversationId: string | null) {
  try {
    // Only cache if we have actual content (not empty placeholder states)
    const validMessages = messages.filter((m) => m.content || m.toolCalls?.length)
    if (!validMessages.length) return
    localStorage.setItem(LS_CACHE_KEY, JSON.stringify({ conversationId, messages: validMessages }))
  } catch { /* quota exceeded — ignore */ }
}

function loadCacheFromLocalStorage(): { conversationId: string | null; messages: ChatMessage[] } | null {
  try {
    const raw = localStorage.getItem(LS_CACHE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as { conversationId: string | null; messages: ChatMessage[] }
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatAttachment {
  mimeType: string
  data: string // base64
  name?: string
}

export interface ChatMessage {
  id: string
  role: "user" | "assistant"
  content: string
  attachments?: ChatAttachment[]
  toolCalls?: ChatToolCall[]
  toolResults?: ChatToolResult[]
  feedback?: "up" | "down"
  createdAt: string
  isStreaming?: boolean
  isPinned?: boolean
}

export interface AiChatState {
  conversationId: string | null
  messages: ChatMessage[]
  isStreaming: boolean
  error: string | null
}

// ---------------------------------------------------------------------------
// SSE Event types
// ---------------------------------------------------------------------------

interface SseTokenEvent {
  text: string
}

interface SseToolCallsEvent {
  calls: ChatToolCall[]
}

interface SseDoneEvent {
  content: string
  conversationId: string | null
  requiresToolExecution?: boolean
}

interface SseErrorEvent {
  error: string
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAiChat(initialConversationId?: string) {
  // Compression state for auto-summarization
  const [compressionState, setCompressionState] = useState<CompressionState>({ isSummarized: false })

  // Instant restore: try localStorage cache synchronously for flicker-free reload
  const [state, setState] = useState<AiChatState>(() => {
    if (initialConversationId) {
      return { conversationId: initialConversationId, messages: [], isStreaming: false, error: null }
    }
    // Try draft first (in-progress streaming that was interrupted)
    const draft = loadDraftFromLocalStorage()
    if (draft && draft.messages.length > 0) {
      return {
        conversationId: draft.conversationId,
        messages: draft.messages
          .filter((m) => !(m.role === "assistant" && !m.content && !m.toolCalls?.length))
          .map((m) => m.isStreaming ? { ...m, isStreaming: false } : m),
        isStreaming: false,
        error: null,
      }
    }
    // Then try cache (last completed conversation)
    const cache = loadCacheFromLocalStorage()
    if (cache && cache.messages.length > 0) {
      return {
        conversationId: cache.conversationId,
        messages: cache.messages.map((m) => m.isStreaming ? { ...m, isStreaming: false } : m),
        isStreaming: false,
        error: null,
      }
    }
    return { conversationId: null, messages: [], isStreaming: false, error: null }
  })

  const abortRef = useRef<AbortController | null>(null)
  const focusRef = useRef<ConversationFocus>({
    lastEntity: null,
    impliedContainer: null,
    recentEntities: [],
  })

  // Auto-load the most recent conversation on mount (or restore draft)
  useEffect(() => {
    if (initialConversationId) return // already specified
    // If state already has messages from synchronous init (localStorage), skip Dexie load
    if (state.messages.length > 0) return
    let cancelled = false
    ;(async () => {
      const latest = await db.chatConversation
        .orderBy("updatedAt")
        .reverse()
        .first()
      if (cancelled || !latest?.id) return
      const messages = await db.chatMessage
        .where("conversationId")
        .equals(latest.id)
        .sortBy("createdAt")
      if (cancelled) return
      setState((prev) => ({
        ...prev,
        conversationId: latest.id!,
        messages: messages.map((m) => ({
          id: m.id!,
          role: m.role as "user" | "assistant",
          content: m.content,
          toolCalls: m.toolCalls,
          toolResults: m.toolResults,
          createdAt: m.createdAt,
        })),
      }))
    })()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Load an existing conversation's messages
  const loadConversation = useCallback(async (convId: string) => {
    // A summary belongs to the conversation it was made from: the one loaded now starts without one.
    setCompressionState({ isSummarized: false })
    const messages = await db.chatMessage
      .where("conversationId")
      .equals(convId)
      .sortBy("createdAt")

    const mapped = messages.map((m) => ({
      id: m.id!,
      role: m.role as "user" | "assistant",
      content: m.content,
      toolCalls: m.toolCalls,
      toolResults: m.toolResults,
      createdAt: m.createdAt,
    }))

    setState((prev) => ({
      ...prev,
      conversationId: convId,
      messages: mapped,
      error: null,
    }))

    // Update cache so this conversation loads instantly on next reload
    saveCacheToLocalStorage(mapped, convId)
  }, [])

  // Send a message and stream the AI response
  const sendMessage = useCallback(
    async (content: string, context?: ChatContext, attachments?: ChatAttachment[], isRetry?: boolean) => {
      // Cancel any in-flight request
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      // One id for this USER MESSAGE, sent with every round of its tool loop: the server counts the message once.
      const turnId = crypto.randomUUID()

      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content,
        attachments: attachments?.length ? attachments : undefined,
        createdAt: new Date().toISOString(),
      }

      // On retry, the user message is already in state — don't add it again
      if (isRetry) {
        setState((prev) => ({
          ...prev,
          isStreaming: true,
          error: null,
        }))
      } else {
        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, userMsg],
          isStreaming: true,
          error: null,
        }))
      }

      // Save draft immediately so messages survive crashes
      const currentMessages = isRetry ? state.messages : [...state.messages, userMsg]
      saveDraftToLocalStorage(currentMessages, state.conversationId)

      // Build messages array for API — strip empty assistant placeholders (failed/empty responses)
      const cleanMessages = currentMessages
        .filter((m) => !(m.role === "assistant" && !m.content && !m.toolCalls?.length))
      try {
        const token = await getValidToken()
        if (!token) throw new Error("Not authenticated")

        // Summary at 15 exchanges, then chained re-summaries (compressionPlan.ts). A new object = a new summary
        // (a chained one keeps isSummarized true, so compare the object, not the flag).
        const newCompressionState = await tryCompressConversation(cleanMessages, compressionState, token)
        if (newCompressionState !== compressionState) {
          setCompressionState(newCompressionState)
          // Persist summary to AI memory system for cross-conversation context
          await persistCompressionToMemory(state.conversationId, cleanMessages, newCompressionState)
        }

        // The summary (if there is one) and EVERYTHING after the part it covers - the new message included.
        // (It used to be a list frozen when the summary was made, so later messages were never sent.)
        const apiMessages = buildRequestMessages(cleanMessages, newCompressionState, toApiMessage)

        const result = await streamAiRequest(
          apiMessages,
          state.conversationId,
          context,
          token,
          controller,
          setState,
          focusRef,
          turnId,
        )

        // Persist to Dexie after stream completes (on retry, find the existing user msg)
        const persistUserMsg = isRetry
          ? (currentMessages[currentMessages.length - 1] ?? userMsg)
          : userMsg
        await persistMessages(
          state.conversationId,
          persistUserMsg,
          {
            id: result.assistantMsgId,
            role: "assistant",
            content: result.content,
            toolCalls: result.toolCalls.length ? result.toolCalls : undefined,
            toolResults: result.toolResults.length ? result.toolResults : undefined,
            createdAt: new Date().toISOString(),
          },
          setState,
        )

        // Clear draft after successful persist to Dexie + update cache for instant reload
        clearDraftFromLocalStorage()
        setState((prev) => {
          saveCacheToLocalStorage(prev.messages, prev.conversationId)
          return prev
        })
      } catch (e) {
        if ((e as Error).name === "AbortError") {
          // Timeout or user-abort — clean up empty placeholder and streaming state
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            messages: prev.messages.filter((m) =>
              !(m.role === "assistant" && !m.content && !m.toolCalls?.length),
            ),
          }))
          return
        }
        console.error("[useAiChat] request failed:", e)
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          // Only a message made for the user is shown; anything else (provider text, "Load failed") is logged
          error:
            e instanceof AiUserMessageError
              ? e.message
              : i18next.t("Something went wrong. Please try again."),
          // Remove empty assistant placeholder (no content = nothing to show)
          messages: prev.messages.filter((m) =>
            !(m.role === "assistant" && !m.content && !m.toolCalls?.length),
          ),
        }))
      }
    },
    [state.messages, state.conversationId, compressionState],
  )

  // Stop streaming
  const stopStreaming = useCallback(() => {
    abortRef.current?.abort()
    setState((prev) => ({ ...prev, isStreaming: false }))
  }, [])

  // Start a new conversation
  const newConversation = useCallback(() => {
    abortRef.current?.abort()
    clearDraftFromLocalStorage()
    // Reset conversation focus
    focusRef.current = { lastEntity: null, impliedContainer: null, recentEntities: [] }
    // Reset compression state
    setCompressionState({ isSummarized: false })
    setState({
      conversationId: null,
      messages: [],
      isStreaming: false,
      error: null,
    })
  }, [])

  // Set feedback on an assistant message (thumbs up/down)
  const setFeedback = useCallback(
    async (messageId: string, feedback: "up" | "down" | undefined) => {
      // Update local state
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((m) =>
          m.id === messageId ? { ...m, feedback } : m,
        ),
      }))
      // Persist to Dexie
      try {
        if (feedback) {
          await db.chatMessage.update(messageId, { feedback })
        } else {
          await db.chatMessage.update(messageId, { feedback: undefined })
        }
      } catch (e) {
        console.error("[useAiChat] Failed to persist feedback:", e)
      }
    },
    [],
  )

  // Retry the last user message after an error — in-place (no visual jumping)
  const retryLast = useCallback(
    (context?: ChatContext) => {
      const lastUserMsg = [...state.messages].reverse().find((m) => m.role === "user")
      if (!lastUserMsg) return

      // Remove the failed assistant message(s) AFTER the last user message, but keep the user message
      const lastUserIdx = state.messages.findIndex((m) => m.id === lastUserMsg.id)
      setState((prev) => ({
        ...prev,
        messages: prev.messages.slice(0, lastUserIdx + 1),
        error: null,
      }))

      // Re-send — but skip adding the user message again (it's already there)
      // We call sendMessage with a special flag by directly invoking the stream
      sendMessage(lastUserMsg.content, context, lastUserMsg.attachments, true)
    },
    [state.messages, sendMessage],
  )

  // Toggle message pin status (for cross-summarization preservation)
  const toggleMessagePin = useCallback(
    async (messageId: string) => {
      const message = state.messages.find((m) => m.id === messageId)
      if (!message || !state.conversationId) return

      const wasPinned = message.isPinned
      try {
        // Update local state optimistically
        setState((prev) => ({
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === messageId ? { ...m, isPinned: !m.isPinned } : m,
          ),
        }))

        // Persist to memory system
        if (wasPinned) {
          await unpinMessage(state.conversationId, messageId)
        } else {
          await pinMessage(state.conversationId, message)
        }
      } catch (e) {
        // Revert on error
        console.error("[useAiChat] Failed to toggle pin:", e)
        setState((prev) => ({
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === messageId ? { ...m, isPinned: wasPinned } : m,
          ),
        }))
      }
    },
    [state.messages, state.conversationId],
  )

  return {
    ...state,
    sendMessage,
    stopStreaming,
    loadConversation,
    newConversation,
    setFeedback,
    retryLast,
    toggleMessagePin,
  }
}

// ---------------------------------------------------------------------------
// Stream AI Request — handles SSE parsing + client-side tool execution loop
// ---------------------------------------------------------------------------

interface StreamResult {
  assistantMsgId: string
  content: string
  toolCalls: ChatToolCall[]
  toolResults: ChatToolResult[]
}

/**
 * Auto-summarize conversation at 15 messages
 */
async function tryCompressConversation(
  messages: ChatMessage[],
  currentCompressionState: CompressionState,
  token: string,
): Promise<CompressionState> {
  // First summary at the threshold; afterwards a chained re-summary once enough messages piled up after it
  const plan = planCompression(messages.length, currentCompressionState)
  if (!plan) return currentCompressionState
  const toSummarize = summarizeRequestMessages(messages, plan)

  try {
    const res = await fetch(`${API_PREFIX}/v1/ai/summarize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        messages: toSummarize,
      }),
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Summarization failed" }))
      console.warn("[useAiChat] Summarization failed:", err)
      return currentCompressionState
    }

    const { summary, intents, completedActions } = await res.json()

    // The summary covers messages [0, plan.to) (a chained re-summary includes the earlier summary); every message
    // after them is sent as it is. Intents and actions accumulate across re-summaries.
    const merge = (a?: string[], b?: string[]) => [...new Set([...(a ?? []), ...(b ?? [])])]
    const newCompressionState: CompressionState = {
      isSummarized: true,
      summary,
      intents: merge(currentCompressionState.intents, intents),
      completedActions: merge(currentCompressionState.completedActions, completedActions),
      summarizedCount: plan.to,
    }

    console.log(
      `[useAiChat] Compression: ${messages.length} messages → summary of ${plan.to}${plan.previousSummary ? " (chained)" : ""} + ${messages.length - plan.to} recent`,
    )

    return newCompressionState
  } catch (e) {
    // Silently fall back to full context on summarization error
    console.warn("[useAiChat] Summarization error:", e instanceof Error ? e.message : String(e))
    return currentCompressionState
  }
}

async function streamAiRequest(
  apiMessages: Array<{
    role: string
    content: string
    attachments?: ChatAttachment[]
    toolCalls?: ChatToolCall[]
    toolResults?: ChatToolResult[]
  }>,
  conversationId: string | null,
  context: ChatContext | undefined,
  token: string,
  controller: AbortController,
  setState: React.Dispatch<React.SetStateAction<AiChatState>>,
  focusRef: React.MutableRefObject<ConversationFocus>,
  turnId: string,
): Promise<StreamResult> {
  const assistantMsgId = crypto.randomUUID()

  // Add placeholder assistant message
  setState((prev) => ({
    ...prev,
    messages: [
      ...prev.messages,
      {
        id: assistantMsgId,
        role: "assistant" as const,
        content: "",
        createdAt: new Date().toISOString(),
        isStreaming: true,
      },
    ],
  }))

  let allToolCalls: ChatToolCall[] = []
  let allToolResults: ChatToolResult[] = []
  let finalContent = ""
  let currentMessages = apiMessages
  let currentToken = token

  const MAX_TOOL_ROUNDS = 5
  const TOOL_TIMEOUT_MS = 60_000 // 60s max per tool call (multi-write tools like createProjectWithTasks can take 40s+ under sync contention)
  const FETCH_TIMEOUT_MS = 60_000 // 60s max for AI response
  const streamStartMs = Date.now()
  let tokenRetried = false // Track if we already did a 401 token refresh

  // Read all memory entries for system prompt injection (non-blocking, fallback to "")
  const memoryMaster = await readAllForPrompt().catch(() => "")

  for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
    // Create a timeout signal that aborts if AI takes too long
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
    const fetchStartMs = Date.now()

    let res: Response
    try {
      res = await fetch(`${API_PREFIX}/v1/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
          // The server counts a message at the edge, before it reads the body: one per turnId, and only a
          // later round of the same message's tool loop is a continuation (not counted again)
          ...chatTurnHeaders(turnId, round),
        },
        body: JSON.stringify(
          buildChatRequestBody({
            messages: currentMessages,
            conversationId,
            turnId,
            memoryMaster,
            context,
            focus: focusRef.current,
          }),
        ),
        signal: controller.signal,
      })
    } finally {
      clearTimeout(timeoutId)
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Request failed" }))
      const errMsg = (err as { error?: string }).error ?? `HTTP ${res.status}`

      // Quota reached / no AI on the plan / too many rounds / the quota cannot be checked: say which, kindly
      const refusal = aiRefusalMessage(res.status, err, i18next.t.bind(i18next))
      if (refusal) throw new AiUserMessageError(refusal)

      // 401 retry: refresh token and retry once (mirrors apiRequest 401 logic)
      if (
        res.status === 401 &&
        !tokenRetried &&
        (errMsg.includes("jwt expired") ||
          errMsg.includes("Token verification failed") ||
          errMsg.includes("Invalid token"))
      ) {
        tokenRetried = true
        clearTokenCache()
        resetRefreshCooldown()
        let refreshed = false
        try {
          await Promise.race([
            syncToRenewToken(),
            new Promise<void>((_, reject) =>
              setTimeout(() => reject(new Error("Token refresh timed out")), 10_000),
            ),
          ])
          refreshed = true
        } catch {
          // Sync-based refresh failed (sync lock held) — try direct token refresh
        }
        let freshToken: string | null = null
        if (refreshed) {
          freshToken = await getValidToken()
        } else {
          // Direct fallback: call Dexie Cloud /token endpoint without sync lock
          freshToken = await directTokenRefresh()
        }
        if (freshToken) {
          currentToken = freshToken
          // Retry this round
          round--
          continue
        }
      }

      logAiRequest({
        endpoint: "/v1/ai/chat",
        method: "POST",
        status: res.status,
        durationMs: Date.now() - fetchStartMs,
        error: errMsg.slice(0, 200),
        streaming: true,
        requestSummary: `round=${round}, messages=${currentMessages.length}`,
      })
      throw new Error(errMsg)
    }

    const reader = res.body?.getReader()
    if (!reader) throw new Error("No response body")

    const { content, toolCalls, requiresToolExecution } =
      await parseSseStream(reader, assistantMsgId, finalContent, setState)

    finalContent = content

    if (!requiresToolExecution || toolCalls.length === 0) {
      // Done — no more tool calls
      setState((prev) => ({
        ...prev,
        isStreaming: false,
        messages: prev.messages.map((m) =>
          m.id === assistantMsgId
            ? {
                ...m,
                content: finalContent,
                toolCalls: allToolCalls.length ? allToolCalls : undefined,
                toolResults: allToolResults.length ? allToolResults : undefined,
                isStreaming: false,
              }
            : m,
        ),
      }))
      break
    }

    // Execute tools locally
    allToolCalls = [...allToolCalls, ...toolCalls]
    const roundResults: ChatToolResult[] = []

    for (const tc of toolCalls) {
      // Bail if request was aborted (e.g. user sent new message)
      if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError")

      // Update UI to show tool is executing
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((m) =>
          m.id === assistantMsgId ? { ...m, toolCalls: allToolCalls } : m,
        ),
      }))

      const execResult = await Promise.race([
        executeToolCall(tc.name, tc.arguments),
        new Promise<string>((_, reject) =>
          setTimeout(() => reject(new Error(`Tool "${tc.name}" timed out after ${TOOL_TIMEOUT_MS / 1000}s`)), TOOL_TIMEOUT_MS),
        ),
        // Also race against abort signal
        new Promise<string>((_, reject) => {
          controller.signal.addEventListener("abort", () => reject(new DOMException("Aborted", "AbortError")), { once: true })
        }),
      ]).catch((e) => {
        if (e instanceof DOMException && e.name === "AbortError") throw e
        return `Error: ${e instanceof Error ? e.message : String(e)}`
      }) as string
      const toolResult: ChatToolResult = {
        toolCallId: tc.id,
        name: tc.name,
        result: execResult,
      }
      roundResults.push(toolResult)
      allToolResults = [...allToolResults, toolResult]

      // Update UI with result
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((m) =>
          m.id === assistantMsgId ? { ...m, toolResults: allToolResults } : m,
        ),
      }))
    }

    // Update conversation focus from this round's tool results
    focusRef.current = extractFocusFromToolResults(roundResults, focusRef.current)

    // Send continuation with tool results
    // Build a new messages array with:
    // - all previous messages
    // - the assistant's tool calls as a message
    // - a "user" message carrying the tool results
    currentMessages = [
      ...currentMessages,
      {
        role: "assistant",
        content: finalContent,
        toolCalls: toolCalls,
      },
      {
        role: "user",
        content: "",
        toolResults: stripRenderData(roundResults),
      },
    ]
  }

  // Log successful completion
  logAiRequest({
    endpoint: "/v1/ai/chat",
    method: "POST",
    status: 200,
    durationMs: Date.now() - streamStartMs,
    streaming: true,
    requestSummary: `messages=${apiMessages.length}, rounds=${allToolCalls.length > 0 ? "multi" : "1"}`,
    responseSummary: finalContent.slice(0, 200),
  })

  return {
    assistantMsgId,
    content: finalContent,
    toolCalls: allToolCalls,
    toolResults: allToolResults,
  }
}

// ---------------------------------------------------------------------------
// SSE Stream Parser
// ---------------------------------------------------------------------------

async function parseSseStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  assistantMsgId: string,
  prevContent: string,
  setState: React.Dispatch<React.SetStateAction<AiChatState>>,
): Promise<{
  content: string
  toolCalls: ChatToolCall[]
  requiresToolExecution: boolean
}> {
  const decoder = new TextDecoder()
  let buffer = ""
  let content = prevContent
  let toolCalls: ChatToolCall[] = []
  let requiresToolExecution = false

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split("\n")
    buffer = lines.pop() ?? ""

    let eventType = ""
    let eventData = ""

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        eventType = line.slice(7)
      } else if (line.startsWith("data: ")) {
        eventData = line.slice(6)

        if (eventType && eventData) {
          try {
            const parsed = JSON.parse(eventData) as unknown

            switch (eventType) {
              case "token": {
                const { text } = parsed as SseTokenEvent
                content += text
                setState((prev) => ({
                  ...prev,
                  messages: prev.messages.map((m) =>
                    m.id === assistantMsgId ? { ...m, content } : m,
                  ),
                }))
                break
              }

              case "tool_calls": {
                const { calls } = parsed as SseToolCallsEvent
                toolCalls = [...toolCalls, ...calls]
                break
              }

              case "done": {
                const doneData = parsed as SseDoneEvent
                if (doneData.content) content = doneData.content
                requiresToolExecution = doneData.requiresToolExecution ?? false
                break
              }

              case "error": {
                const { error } = parsed as SseErrorEvent
                console.error("[useAiChat] stream error:", error)
                throw new AiUserMessageError(aiStreamErrorMessage(i18next.t.bind(i18next), error))
              }
            }
          } catch (e) {
            if (e instanceof Error && e.message !== "Unexpected end of JSON input") {
              throw e
            }
          }
          eventType = ""
          eventData = ""
        }
      } else if (line === "") {
        eventType = ""
        eventData = ""
      }
    }
  }

  return { content, toolCalls, requiresToolExecution }
}

// ---------------------------------------------------------------------------
// Persist messages to Dexie
// ---------------------------------------------------------------------------

async function persistMessages(
  conversationId: string | null,
  userMsg: ChatMessage,
  assistantMsg: ChatMessage,
  setState: React.Dispatch<React.SetStateAction<AiChatState>>,
) {
  try {
    let convId = conversationId

    if (!convId) {
      // Create a new conversation
      convId = crypto.randomUUID()
      const conversation: DbChatConversation = {
        id: convId,
        title: userMsg.content.slice(0, 100),
        summaryTitle: generateSummaryTitle(userMsg.content, assistantMsg.content),
        preview: assistantMsg.content.slice(0, 200),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messageCount: 2,
        pinned: 0,
      }
      await db.chatConversation.put(conversation)
      setState((prev) => ({ ...prev, conversationId: convId }))
    } else {
      // Update existing conversation
      await db.chatConversation.update(convId, {
        updatedAt: new Date().toISOString(),
        preview: assistantMsg.content.slice(0, 200),
      })
      const conv = await db.chatConversation.get(convId)
      if (conv) {
        await db.chatConversation.update(convId, {
          messageCount: (conv.messageCount ?? 0) + 2,
        })
      }
    }

    // Save messages
    const userDbMsg: DbChatMessage = {
      id: userMsg.id,
      conversationId: convId!,
      role: "user",
      content: userMsg.content,
      createdAt: userMsg.createdAt,
    }
    const assistantDbMsg: DbChatMessage = {
      id: assistantMsg.id,
      conversationId: convId!,
      role: "assistant",
      content: assistantMsg.content,
      toolCalls: assistantMsg.toolCalls,
      toolResults: assistantMsg.toolResults,
      createdAt: assistantMsg.createdAt,
    }

    await db.chatMessage.bulkPut([userDbMsg, assistantDbMsg])
  } catch (e) {
    console.error("[useAiChat] Failed to persist messages:", e)
  }
}

// ---------------------------------------------------------------------------
// Smart title generation — derives a concise title from the first exchange
// ---------------------------------------------------------------------------

function generateSummaryTitle(userMessage: string, _assistantResponse: string): string {
  const msg = userMessage.trim()

  // If the user message is short enough, it IS the title (e.g. "What's in inbox?")
  if (msg.length <= 40) return msg

  // For longer messages, take first sentence or clause (works for any language)
  const firstSentence = msg.split(/[.!?\n]/)[0]
  if (firstSentence && firstSentence.length <= 50) return firstSentence.trim()

  // Try first clause before comma/dash
  const firstClause = msg.split(/[,\-–—]/)[0]
  if (firstClause && firstClause.length <= 50) return firstClause.trim()

  // Last resort: truncate with ellipsis
  return msg.slice(0, 45).trim() + "…"
}
